import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import Icon from '../../components/Icon';
import PulseDot from '../../components/PulseDot';
import SeverityBadge from '../../components/SeverityBadge';
import TrendChart from '../../components/TrendChart';
import { formatDateTime, timeAgo } from '../../lib/time';

const money = (n) => '₺' + Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
      <h3 style={{ fontSize: '0.98rem', marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}

export default function AdminCustomer() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [loading, setLoading] = useState(true);
  const [c, setC] = useState(null);
  const [disc, setDisc] = useState({ pct: '', note: '' });
  const [free, setFree] = useState({ days: '30', note: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');

  const [mon, setMon] = useState(null);
  const [monServer, setMonServer] = useState(null);
  const [slots, setSlots] = useState('');

  const load = () => api.adminCustomer(id).then((r) => {
    setC(r.customer);
    const sub = r.customer.subscriptions[0];
    if (sub && slots === '') setSlots(String(sub.serverSlots ?? 1));
  });
  useEffect(() => { load().finally(() => setLoading(false)); }, [id]);
  useEffect(() => {
    api.adminCustomerMonitoring(id, '7d', monServer || undefined).then(setMon).catch(() => setMon({ hasLicense: false }));
  }, [id, monServer]);

  const setServerSlots = async () => {
    setBusy('slots');
    try {
      const r = await api.adminSetSlots(id, Number(slots));
      flash('ok', t('admin.slots_done', { n: r.serverSlots }));
      await load();
    } catch (e) { flash('crit', e.message); } finally { setBusy(''); }
  };

  const flash = (tone, text) => { setMsg({ tone, text }); setTimeout(() => setMsg(null), 8000); };

  const grantDiscount = async () => {
    setBusy('disc');
    try {
      const r = await api.adminGrantDiscount(id, { discountPercent: disc.pct, note: disc.note || undefined });
      flash('ok', t('admin.grant_discount_done', { code: r.coupon.code, pct: r.coupon.discountPercent }));
      setDisc({ pct: '', note: '' });
      await load();
    } catch (e) { flash('crit', e.message); } finally { setBusy(''); }
  };
  const grantFree = async () => {
    setBusy('free');
    try {
      const r = await api.adminGrantFreeTime(id, { days: free.days, note: free.note || undefined });
      flash('ok', t('admin.grant_free_done', { days: free.days, date: formatDateTime(r.subscription.currentPeriodEnd, lang) }));
      await load();
    } catch (e) { flash('crit', e.message); } finally { setBusy(''); }
  };

  if (loading) {
    return <DashboardLayout title={t('admin.customer_title')} crumb={t('admin.title')} heading={false}><Skeleton className="sk-block" style={{ marginTop: 20 }} /></DashboardLayout>;
  }
  if (!c) {
    return <DashboardLayout title={t('admin.customer_title')} crumb={t('admin.title')} heading={false}>
      <div className="empty-state"><h3>404</h3><Link to="/admin" className="btn btn-sm">{t('admin.back')}</Link></div>
    </DashboardLayout>;
  }

  return (
    <DashboardLayout title={c.email} crumb={t('admin.title')} heading={false}>
      <Link to="/admin" style={{ fontSize: 13, color: 'var(--text-3)' }}>← {t('admin.back')}</Link>
      <h1 className="page-title" style={{ marginTop: 8 }}>{c.displayName || c.email}</h1>
      <p className="page-sub">{c.email} · {t('admin.col_joined')}: {formatDateTime(c.createdAt, lang)}</p>

      {msg && (
        <div className="form-error" style={{ background: msg.tone === 'ok' ? 'var(--ok-soft)' : 'var(--crit-soft)', borderColor: 'transparent', color: msg.tone === 'ok' ? 'var(--ok)' : 'var(--crit)' }}>
          <Icon name={msg.tone === 'ok' ? 'check' : 'alert-triangle'} size={14} /> {msg.text}
        </div>
      )}

      <div className="stat-row">
        <div className="card stat">
          <div className="stat-label">{t('admin.detail_revenue')}</div>
          <div className="stat-value">{money(c.totalRevenue)}</div>
        </div>
        <div className="card stat">
          <div className="stat-label">{t('admin.detail_referrals')}</div>
          <div className="stat-value">{c.referrals.length} <span style={{ fontSize: 13, color: 'var(--text-3)' }}>({c.referrals.filter((r) => r.converted).length} ✓)</span></div>
        </div>
      </div>

      <Section title={t('admin.detail_subs')}>
        {c.subscriptions.length === 0 ? <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>—</p> : (
          <div className="list">
            {c.subscriptions.map((s) => (
              <div className="list-row" key={s.id} style={{ padding: '10px 0' }}>
                <span className={`badge ${s.status === 'active' ? 'ok' : s.status === 'past_due' ? 'warn' : 'crit'}`}>{t(`license.sub_${s.status}`)}</span>
                <div className="row-main">
                  {t(`checkout.plan_${s.plan}`)}
                  {s.serverSlots > 1 && <span style={{ color: 'var(--text-3)', fontSize: 12 }}> · {t('admin.slots_n', { n: s.serverSlots })}</span>}
                  {s.coupon && <span style={{ color: 'var(--text-3)', fontSize: 12 }}> · {s.coupon.code} ({s.coupon.discountPercent}%)</span>}
                </div>
                <div className="row-time">{formatDateTime(s.currentPeriodEnd, lang)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title={t('admin.detail_licenses')}>
        {c.licenses.map((l) => (
          <div key={l.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', flexWrap: 'wrap', gap: 8 }}>
            <code style={{ fontSize: 13, color: l.status === 'active' ? 'var(--accent)' : 'var(--text-3)' }}>{l.key}</code>
            <span className="status-line">
              <PulseDot tone={l.status !== 'active' ? 'idle' : l.agentStatus === 'active' ? 'ok' : l.agentStatus === 'silent' ? 'crit' : 'idle'} animated={false} />
              {l.serverLabel || '—'}
              {l.status !== 'active' && <span className="srv-tag" style={{ marginLeft: 6 }}>{t('servers.suspended')}</span>}
              {' · '}{l.lastHeartbeatAt ? timeAgo(l.lastHeartbeatAt, lang) : t('admin.never_connected')}
            </span>
          </div>
        ))}
        {c.subscriptions[0] && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
            <div className="field" style={{ marginBottom: 0, maxWidth: 130 }}>
              <label>{t('admin.slots_label')}</label>
              <input type="number" min="1" max="50" value={slots} onChange={(e) => setSlots(e.target.value)} />
            </div>
            <button className="btn btn-subtle btn-sm" onClick={setServerSlots} disabled={busy === 'slots'}>
              {t('admin.slots_set')}
            </button>
          </div>
        )}
      </Section>

      {mon && mon.hasLicense && (
        <>
          {mon.servers && mon.servers.length > 1 && (
            <div className="field" style={{ marginBottom: 'var(--sp-3)', maxWidth: 260 }}>
              <label>{t('adminMon.pick_server')}</label>
              <select value={monServer || mon.license.key} onChange={(e) => setMonServer(e.target.value)}>
                {mon.servers.map((s) => (
                  <option key={s.key} value={s.key}>{s.label || s.key}{s.status !== 'active' ? ` (${t('servers.suspended')})` : ''}</option>
                ))}
              </select>
            </div>
          )}
          {(mon.health || mon.license?.schemaHealth) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
              {mon.health && (
                <span className={`badge ${mon.health.label}`}>
                  {t('health.title')}: {mon.health.score}/100
                </span>
              )}
              {mon.license?.schemaHealth && (
                <span className="badge">
                  {t('dash.schema_found', { found: mon.license.schemaHealth.found, total: mon.license.schemaHealth.total })}
                </span>
              )}
              {mon.license?.serverProfile?.maxLevel && (
                <span className="badge">{t('dash.level_cap', { n: mon.license.serverProfile.maxLevel })}</span>
              )}
            </div>
          )}

          <Section title={t('adminMon.cust_economy')}>
            <TrendChart data={(mon.series?.economy_production) || []} />
            {(mon.series?.active_players?.length > 0) && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 6 }}>{t('dashboard.chart_players')}</div>
                <TrendChart data={mon.series.active_players} color="var(--info)" />
              </div>
            )}
          </Section>

          <Section title={t('adminMon.cust_alerts')}>
            {(!mon.alerts || mon.alerts.length === 0) ? (
              <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('dashboard.no_alerts')}</p>
            ) : (
              <div className="list">
                {mon.alerts.slice(0, 15).map((a) => (
                  <div className="list-row" key={a.id} style={{ padding: '8px 0' }}>
                    <SeverityBadge severity={a.severity} />
                    <div className="row-main">
                      <div style={{ fontWeight: 500 }}>{t(`metric.${a.type}`, a.type)}</div>
                      <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{a.message}</div>
                    </div>
                    <div className="row-time">{formatDateTime(a.triggeredAt, lang)}</div>
                  </div>
                ))}
              </div>
            )}
            {mon.alerts?.length > 0 && (
              <Link to={`/dashboard/alerts?customer=${id}`} className="btn btn-subtle btn-sm" style={{ marginTop: 12 }}>
                {t('dashboard.view_all')}
              </Link>
            )}
          </Section>

          {mon.channels?.length > 0 && (
            <Section title={t('adminMon.cust_channels')}>
              <div className="list">
                {mon.channels.map((ch) => (
                  <div className="list-row" key={ch.id} style={{ padding: '8px 0' }}>
                    <span className={`badge ${ch.enabled ? 'ok' : ''}`}>{t(`notifications.type_${ch.type}`, ch.type)}</span>
                    <div className="row-main"><code style={{ fontSize: 12, color: 'var(--text-3)' }}>{ch.target}</code></div>
                    <div className="row-time">{ch.enabled ? t('notifications.enabled') : t('notifications.disabled')}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      <Section title={t('admin.detail_tx')}>
        {c.transactions.length === 0 ? <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>—</p> : (
          <div className="list">
            {c.transactions.slice(0, 12).map((tx) => (
              <div className="list-row" key={tx.id} style={{ padding: '8px 0' }}>
                <span className={`badge ${tx.status === 'success' ? 'ok' : tx.status === 'failed' ? 'crit' : ''}`}>{t(`license.tx_${tx.status}`)}</span>
                <div className="row-main">{money(tx.amount)}</div>
                <div className="row-time">{formatDateTime(tx.createdAt, lang)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {c.grantedCoupons.length > 0 && (
        <Section title={t('admin.detail_granted')}>
          <div className="list">
            {c.grantedCoupons.map((g) => (
              <div className="list-row" key={g.code} style={{ padding: '8px 0' }}>
                <span className={`badge ${g.active ? 'ok' : ''}`}>{g.discountPercent}%</span>
                <div className="row-main"><code style={{ color: 'var(--accent)', fontSize: 12.5 }}>{g.code}</code> <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{g.note}</span></div>
                <div className="row-time">{g.redeemedCount > 0 ? t('referral.reward_used') : ''}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Ödül aksiyonları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--sp-4)' }}>
        <Section title={t('admin.grant_discount_title')}>
          <div className="field"><label>{t('admin.grant_discount_pct')}</label>
            <input type="number" min="1" max="100" value={disc.pct} onChange={(e) => setDisc({ ...disc, pct: e.target.value })} /></div>
          <div className="field"><label>{t('admin.grant_discount_note')}</label>
            <input value={disc.note} onChange={(e) => setDisc({ ...disc, note: e.target.value })} /></div>
          <button className="btn btn-subtle btn-sm" onClick={grantDiscount} disabled={busy === 'disc' || !disc.pct}>
            <Icon name="ticket" size={13} /> {t('admin.grant_discount_btn')}
          </button>
        </Section>

        <Section title={t('admin.grant_free_title')}>
          <div className="field"><label>{t('admin.grant_free_days')}</label>
            <input type="number" min="1" max="366" value={free.days} onChange={(e) => setFree({ ...free, days: e.target.value })} /></div>
          <div className="field"><label>{t('admin.grant_discount_note')}</label>
            <input value={free.note} onChange={(e) => setFree({ ...free, note: e.target.value })} /></div>
          <button className="btn btn-subtle btn-sm" onClick={grantFree} disabled={busy === 'free' || !free.days}>
            <Icon name="gift" size={13} /> {t('admin.grant_free_btn')}
          </button>
        </Section>
      </div>
    </DashboardLayout>
  );
}
